<template>
  <component :is="as" ref="target">
    <slot :content-box-size="contentBoxSize" :border-box-size="borderBoxSize" />
  </component>
</template>

<script setup lang="ts">
const emit = defineEmits([
  'observer-created',
  'observer-unobserve',
  'size-change',
])

const target = useTemplateRef<Element>('target')

const resizeObserver = ref<ResizeObserver | null>(null)

const props = withDefaults(
  defineProps<{
    as?: string
    initSize?: { width: number; height: number }
    timeout?: number
  }>(),
  {
    as: 'div',
    initSize: () => ({
      width: 1024,
      height: 768,
    }),
    timeout: 100,
  },
)

const contentBoxSize = ref(props.initSize)
const borderBoxSize = ref(props.initSize)

const createResizeObserver = () => {
  if (resizeObserver.value?.observe) {
    // already created
    return
  }

  resizeObserver.value = new ResizeObserver((entries) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        for (const entry of entries) {
          if (entry.contentBoxSize) {
            // Firefox implements `contentBoxSize` as a single content rect, rather than an array
            const contentBoxSize = Array.isArray(entry.contentBoxSize)
              ? entry.contentBoxSize[0]
              : entry.contentBoxSize

            const borderBoxSize = Array.isArray(entry.borderBoxSize)
              ? entry.borderBoxSize[0]
              : entry.borderBoxSize

            contentBoxSize.value = {
              width: contentBoxSize.inlineSize,
              height: contentBoxSize.blockSize,
            }

            borderBoxSize.value = {
              width: borderBoxSize.inlineSize,
              height: borderBoxSize.blockSize,
            }
          } else {
            // Safari
            contentBoxSize.value = {
              width: entry.contentRect.width,
              height: entry.contentRect.height,
            }
            const target = entry?.target as HTMLElement
            borderBoxSize.value = {
              width: target?.offsetWidth,
              height: target?.offsetHeight,
            }
          }
          emit('size-change', {
            contentBoxSize: contentBoxSize.value,
            borderBoxSize: borderBoxSize.value,
          })
        }
      })
    })
  })

  if (target.value) resizeObserver.value?.observe(target.value)
  emit('observer-created', {
    target: target.value,
    resizeObserver: resizeObserver.value,
  })
}

const unobserveResizeObserver = () => {
  if (resizeObserver.value?.unobserve && target.value) {
    resizeObserver.value?.unobserve?.(target.value)
    emit('observer-unobserve')
  }
}

onMounted(() => {
  createResizeObserver()
})

onBeforeUnmount(() => {
  unobserveResizeObserver()
})
</script>

<style lang="scss" module>
.dev {
  position: relative;
}

.overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  z-index: 1;

  &.borderBoxSizeOverlay {
    border: 2px dotted rgba(#ffd04d, 1);
  }

  &.contentBoxSizeOverlay {
    border: 2px dashed rgba(#94d657, 1);
  }
}

.values {
  position: absolute;
  top: 0;
  right: 0;
  padding: 1em;
  z-index: 1;
}
</style>
